import React, { ReactNode } from "react";
import {
  Button,
  ButtonGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DaysOfWeek, AUTURA_ASPHALT_DARK } from "utils";
import { DayOfWeek } from "types";
import { useTranslation } from "react-i18next";

export interface DaySelectProps {
  days: DayOfWeek[];
  setDays: React.Dispatch<React.SetStateAction<DayOfWeek[]>>;
}

export enum DayPattern {
  WEEKDAYS = "WEEKDAYS",
  WEEKENDS = "WEEKENDS",
  ALL = "ALL",
}

const DaySelect = ({ days, setDays }: DaySelectProps): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("lg"));
  const handleDayPatternChange = (pattern: DayPattern) => {
    switch (pattern) {
      case DayPattern.ALL:
        setDays(DaysOfWeek);
        break;
      case DayPattern.WEEKDAYS:
        setDays(
          DaysOfWeek.filter(
            (d) => d !== DayOfWeek.SATURDAY && d !== DayOfWeek.SUNDAY,
          ),
        );
        break;
      case DayPattern.WEEKENDS:
        setDays(
          DaysOfWeek.filter(
            (d) => d === DayOfWeek.SATURDAY || d === DayOfWeek.SUNDAY,
          ),
        );
        break;
    }
  };

  const handleDayChange = (
    event: React.MouseEvent<HTMLElement>,
    newDays: DayOfWeek[],
  ) => {
    setDays(newDays);
  };

  return (
    <Stack direction={"column"}>
      <ButtonGroup
        orientation="horizontal"
        color={"secondary"}
        variant="outlined"
        size={mobile ? "medium" : "large"}
        aria-label={t("rule.data.enforcementSchedule.dayPattern")}
        sx={{
          display: "flex",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderBottom: "0.5px solid black",
        }}
      >
        <Button
          key={DayPattern.ALL}
          aria-label={t("everyday")}
          sx={{
            borderBottomLeftRadius: 0,
            flex: 1,
            border: "0.5px solid black",
          }}
          onClick={() => handleDayPatternChange(DayPattern.ALL)}
        >
          {t("everyday")}
        </Button>
        <Button
          key={DayPattern.WEEKDAYS}
          aria-label={t("weekdays")}
          sx={{ flex: 1, border: "0.5px solid black" }}
          onClick={() => handleDayPatternChange(DayPattern.WEEKDAYS)}
        >
          {t("weekdays")}
        </Button>
        <Button
          key={DayPattern.WEEKENDS}
          aria-label={t("weekends")}
          sx={{
            borderBottomRightRadius: 0,
            flex: 1,
            border: "0.5px solid black",
          }}
          onClick={() => handleDayPatternChange(DayPattern.WEEKENDS)}
        >
          {t("weekends")}
        </Button>
      </ButtonGroup>
      <ToggleButtonGroup
        orientation="horizontal"
        sx={{ display: "flex" }}
        value={days}
        size={"small"}
        color={"primary"}
        onChange={handleDayChange}
        aria-label={t("daysOfWeek")}
      >
        {DaysOfWeek.map((d) => {
          return (
            <ToggleButton
              key={d}
              value={d}
              aria-label={d}
              sx={{
                flex: 1,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                border: "0.5px solid",
                borderColor: "secondary.main",
                borderTop: "none",
              }}
            >
              {t(`${d}_shortest`)}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Stack>
  );
};

export default DaySelect;
